import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { STATIC_PLANS } from '../data/staticPlans';
import { errorMessage } from '../services/apiError';
import { getCachedDetectedCountry, getDetectedCountry } from '../services/countryService';
import { subscriptionService, PlanConfig, UserSubscription } from '../services/subscriptionService';
import { isIOSApp } from '../utils/platformDetection';
import {
  isCountryReadyForPricing,
  normalizeCountryCode,
  resolveBillingCountry,
} from '../utils/pricingCountry';

type PricingState = {
  plans: Record<string, PlanConfig>;
  sub: UserSubscription | null;
  plansLoaded: boolean;
  subscriptionLoadedForAuth: boolean | null;
  error: string | null;
  detectedCountry: string | null;
  countryDetectionSettled: boolean;
};

type PricingStateSetter = Dispatch<SetStateAction<PricingState>>;

type CountryReadiness = {
  billingCountry: string | null;
  countryReady: boolean;
};

type PricingResultInput = {
  state: PricingState;
  setState: PricingStateSetter;
  isAuthenticated: boolean;
  authLoading: boolean;
  billingCountry: string | null;
  countryReady: boolean;
};

const createInitialState = (): PricingState => {
  const cachedCountry = getCachedDetectedCountry();

  return {
    plans: STATIC_PLANS,
    sub: null,
    plansLoaded: false,
    subscriptionLoadedForAuth: null,
    error: null,
    detectedCountry: cachedCountry,
    countryDetectionSettled: Boolean(cachedCountry),
  };
};

const fetchSub = (auth: boolean) => (
  auth ? subscriptionService.getCurrentSubscription().catch(() => null) : Promise.resolve(null)
);

/** Accept API plans only when they look like a valid Record<string, PlanConfig>. */
const isValidPlans = (p: unknown): p is Record<string, PlanConfig> => typeof p === 'object' && p !== null && 'free' in p &&
  typeof (p as Record<string, PlanConfig>).free?.monthly_credits === 'number';

const getCountryReadiness = (
  userCountry: string | null,
  state: PricingState,
): CountryReadiness => {
  const billingCountry = resolveBillingCountry(userCountry, state.detectedCountry);
  const countryReady = isCountryReadyForPricing({
    userCountry,
    detectedCountry: state.detectedCountry,
    detectionSettled: state.countryDetectionSettled,
    isIOSAppEnvironment: isIOSApp(),
  });

  return { billingCountry, countryReady };
};

const useLoadPlansAndSubscription = (
  authLoading: boolean,
  isAuthenticated: boolean,
  setState: PricingStateSetter,
) => {
  useEffect(() => {
    if (authLoading) return;

    let active = true;

    Promise.all([subscriptionService.getPlans(), fetchSub(isAuthenticated)])
      .then(([p, s]) => active && setState((prev) => ({
        ...prev,
        plans: isValidPlans(p) ? p : STATIC_PLANS,
        sub: isAuthenticated ? s : null,
        plansLoaded: true,
        subscriptionLoadedForAuth: isAuthenticated,
        error: null,
      })))
      .catch((err) => active && setState((prev) => ({
        ...prev,
        sub: null,
        plansLoaded: true,
        subscriptionLoadedForAuth: isAuthenticated,
        error: Object.keys(prev.plans).length > 0
          ? null
          : errorMessage(err, isIOSApp()
            ? 'We couldn’t load pricing. Check your connection and try again.'
            : 'We couldn’t load pricing. Refresh and try again.'),
      })));

    return () => { active = false; };
  }, [authLoading, isAuthenticated, setState]);
};

const useLoadDetectedCountry = (
  authLoading: boolean,
  userCountry: string | null,
  state: PricingState,
  setState: PricingStateSetter,
) => {
  useEffect(() => {
    if (authLoading || userCountry || state.countryDetectionSettled || state.detectedCountry) return;

    let active = true;

    getDetectedCountry()
      .then((country) => {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          detectedCountry: normalizeCountryCode(country),
          countryDetectionSettled: true,
        }));
      })
      .catch(() => {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          detectedCountry: null,
          countryDetectionSettled: true,
        }));
      });

    return () => { active = false; };
  }, [authLoading, setState, state.countryDetectionSettled, state.detectedCountry, userCountry]);
};

const buildPricingDataResult = ({
  state,
  setState,
  isAuthenticated,
  authLoading,
  billingCountry,
  countryReady,
}: PricingResultInput) => {
  const loading = authLoading
    || (isAuthenticated && state.subscriptionLoadedForAuth !== true)
    || (isIOSApp() && !state.plansLoaded);
  const pricingReady = !loading && countryReady;

  return {
    plans: state.plans,
    detectedCountry: state.detectedCountry,
    billingCountry,
    pricingReady,
    currentSub: isAuthenticated ? state.sub : null,
    setCurrentSub: (s: UserSubscription | null) => setState((prev) => ({
      ...prev,
      sub: s,
      subscriptionLoadedForAuth: isAuthenticated,
    })),
    loading,
    error: state.error,
    isAuthenticated,
  };
};

export const usePricingData = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [state, setState] = useState<PricingState>(createInitialState);
  const userCountry = normalizeCountryCode(user?.country);
  const { billingCountry, countryReady } = getCountryReadiness(userCountry, state);

  useLoadPlansAndSubscription(authLoading, isAuthenticated, setState);
  useLoadDetectedCountry(authLoading, userCountry, state, setState);

  return buildPricingDataResult({
    state, setState, isAuthenticated, authLoading, billingCountry, countryReady,
  });
};
